/* eslint-disable react/jsx-props-no-spreading */
import { Grid, GridProps, Typography, makeStyles } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import {
  CreditCardData,
  DefaultButton,
  MessageBox,
  PhotoCard,
  colors,
  useFormHook,
} from 'design-system';
import React, { useEffect, useMemo, useState } from 'react';
import { Copy } from 'react-feather';
import { ConnectedProps, connect } from 'react-redux';
import PersonDataResponse from 'src/models/responseTypes/PersonValidationResponse';
// Analytics
import {
  BANK_ISSUER,
  CARD_TYPE,
  PAYER,
  PAYMENT_DATA_EMISSION_STEP,
  PAYMENT_FREQUENCY,
  PAYMENT_METHOD,
  PAY_DAY,
  QUOTATION_ID,
  SELECTED_PACKAGE,
  UPSELLING_ENDORSEMENT,
  trackCurrentStep,
  trackEvent,
} from 'src/analytics';

import { Quotation } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

// Constants
import { creditCard } from 'src/constants/paymentTypes';
import {
  REQUEST_FRACTIONATION,
  REQUEST_UPLOAD_PAYMENT_FILE,
  REQUEST_UPSELLING,
} from 'src/store/emissionPolicy/requestIdentifiers';

// Redux
import { setACHData } from 'src/store/achData/actions';
import {
  invalidateCreditCard,
  setPaymentData,
} from 'src/store/creditCardData/actions';
import {
  requestFractionation,
  requestUpselling,
  setAdditionalDocumentACH,
  setNewPayerDataFields,
  setPaymentIssuer,
  updateAllValues,
} from 'src/store/emissionPolicy/actions';
import { PaymentDataFields } from 'src/store/emissionPolicy/slice/emissionPolicySlice';
import { RootState } from 'src/store/rootReducer';

// Models
import ACHData from 'src/models/helperTypes/ACHData';
import { Fraction } from 'src/models/helperTypes/FractionationType';
import PaymentIssuer from 'src/models/helperTypes/PaymentIssuer';

// functions
import { findFractionByQuantity } from 'src/functions/findFractionByQuantity';

import { PLAT_ENDORSEMENT } from 'src/constants/endorsementCodes';
import profiles from 'src/constants/profileTypes';
import {
  getOptionName,
  getPaymentTypeName,
} from 'src/functions/individualOptionHelpers';
import { verifyBranchOffice } from 'src/functions/selectValidationPaymentEmission';
import { requestSearchValidatePerson } from 'src/store/person/actions';
import {
  PaymentMethodForm,
  makePaymentQuantityCard,
  makePolicyStartDateField,
  updatePaymentData,
} from './viewMethods';

// Methods
import {
  flattenData,
  makeDates,
  parseToACHDataFields,
  parseToCreditCardFields,
  parseToPaymentDataFields,
  shouldOpenUpsellingModal,
} from './dataMethods';

// Helpers
import FormShape from './FormShape';
import makeValidationSchema from './makeValidationSchema';
import { TrackEventData, makeEmptyTrackEvent } from './tractEventPayment';

// Components
import SnackbarMessage from '../../components/SnackbarMessage';
import useBaseWrapperStyles from '../useBaseWrapperStyles';
import ConsentModal from './components/ConsentModal';
import PaymentMessageAlert from './components/PaymentAlertMessage';
import PaymentIssuerSelectionCards from './components/PaymentIssuerSelectionCards';
import SignAlertMessage from './components/SignMessage';
import UpsellingModal from './components/UpsellingModal';

const useStyles = makeStyles(() => ({
  autoPxMessage: {
    fontWeight: 700,
    fontSize: 12,
  },
  copyLinkMessage: {
    fontWeight: 400,
    fontSize: 15,
  },
}));

const sharedInputDependency = ['type'];
const inputDependencies = {
  cardName: sharedInputDependency,
  cardNumber: sharedInputDependency,
  expirationDate: sharedInputDependency,
  securityCode: sharedInputDependency,
  bankEntityCode: sharedInputDependency,
  cardAddress: sharedInputDependency,
};

function renderSubmitButton(
  handleSubmit: (event?: React.SyntheticEvent | undefined) => void,
  buttonIsDisabled: boolean,
): JSX.Element {
  return (
    <DefaultButton
      id="sales-web-inspection-button-next"
      data-testid="submit-button"
      onClick={handleSubmit}
      disabled={buttonIsDisabled}
      fullWidth
    >
      Siguiente
    </DefaultButton>
  );
}

const gridProps: GridProps = {
  container: true,
  spacing: 2,
  item: true,
  md: 7,
  alignItems: 'stretch',
  justifyContent: 'center',
};

export function TrackEventFunction(
  data: TrackEventData,
  selectedPackage: string,
): void {
  trackEvent({
    eventName: PAYMENT_DATA_EMISSION_STEP,
    eventProperties: {
      [PAYER]: data.paymentIssuerSource,
      [PAYMENT_METHOD]: getPaymentTypeName(data.type),
      [CARD_TYPE]: data.cardType,
      [BANK_ISSUER]: data.bankEntityCode,
      [PAYMENT_FREQUENCY]: data.paymentQuantityType,
      [PAY_DAY]: data.paymentDayNumber,
      [SELECTED_PACKAGE]: selectedPackage,
    },
  });
  trackCurrentStep(PAYMENT_DATA_EMISSION_STEP);
}

export function TrackUpsellingEventFunction(quotationId: string): void {
  trackEvent({
    eventName: UPSELLING_ENDORSEMENT,
    eventProperties: {
      [QUOTATION_ID]: quotationId,
    },
  });
}

export const PaymentData = ({
  paymentDataFields,
  onSubmit,
  updateAllValuesAction,
  quotation,
  creditCardData,
  achData,
  paymentIssuerSource,
  paymentIssuer,
  setPaymentDataAction,
  setACHDataAction,
  invalidateCreditCardAction,
  setPaymentIssuerAction,
  exclusionDate,
  requestFractionationAction,
  fractionations,
  accepted,
  uniqueId,
  additionalDocumentACHFileName,
  additionalDocumentACHBase64,
  setAdditionalDocumentACHAction,
  requestUpsellingAction,
  personValidateData,
  validatePersonAction,
  setNewPayerDataFieldsAction,
}: Props): JSX.Element => {
  const [openCopySnack, setOpenCopySnack] = useState(false);
  const [curPaymentIssuer, updateCurPaymentIssuer] = useState(paymentIssuer);
  const [openModal, setOpenModal] = useState(false);
  const [eventData, setEventData] = useState<TrackEventData>(
    makeEmptyTrackEvent(),
  );
  const [upsellingModalOpened, setUpsellingModalOpened] = useState(false);
  const [openUpsellingModal, setOpenUpsellingModal] = useState(false);

  const upsellingStatus = useNetworkObserver(REQUEST_UPSELLING);

  const [paymentFraction, setPaymentFraction] = useState<number | null>(null);

  const packageCoverages = quotation.units.find(
    (x) => x.package.id === quotation.packageOption,
  )?.coverages;

  const profileType = quotation.quotationProfile.type;

  useEffect(() => {
    if (
      !upsellingModalOpened &&
      !packageCoverages?.find((x) => x.coverageId === PLAT_ENDORSEMENT)
    ) {
      setOpenUpsellingModal(true);
      setUpsellingModalOpened(true);
    }
  }, [packageCoverages, upsellingModalOpened]);

  useEffect(() => {
    window.scrollTo(0, 0);
    invalidateCreditCardAction();
    requestFractionationAction();
  }, [invalidateCreditCardAction, requestFractionationAction]);

  const requestFractionationStatus = useNetworkObserver(REQUEST_FRACTIONATION);
  const paymentFileStatus = useNetworkObserver(REQUEST_UPLOAD_PAYMENT_FILE);

  useEffect(() => {
    if (upsellingStatus === 'success') {
      window.location.reload();
    }
  });

  const { today, twoMonthsAhead } = useMemo(
    () => makeDates(exclusionDate),
    [exclusionDate],
  );
  const classes = useStyles();
  const wrapperClasses = useBaseWrapperStyles();

  function handleEffectSubmit(): void {
    TrackEventFunction(eventData, getOptionName(quotation.packageOption));
    onSubmit();
  }

  function handleEffectUpselling(): void {
    TrackUpsellingEventFunction(quotation.id.toString());
    requestUpsellingAction();
  }

  /**
   * Called when the form submits
   */
  function handleFormSubmit(data: FormShape): void {
    updateAllValuesAction(parseToPaymentDataFields(data));
    setPaymentDataAction(parseToCreditCardFields(data));
    setPaymentIssuerAction(data.paymentIssuerSource, curPaymentIssuer);
    setACHDataAction(parseToACHDataFields(data));
    if (quotation.customerType === 'N') {
      setEventData(data);
      setOpenModal(true);
    } else {
      TrackEventFunction(data, getOptionName(quotation.packageOption));
      onSubmit();
    }
  }

  /**
   * Creates the form manager with required input dependencies.
   * Note that, since all properties from the credit card depend on whether the 'type' field
   * changes, they need to register it as an input dependency, or else the form can get stuck when
   * changing payment types with previously invalid input.
   *
   * To prevent the form from aggressively showing all errors, we will defer error checks until the
   * submit button is pressed.
   */
  const {
    getInputProps,
    handleSubmit,
    providedAllRequired,
    updateMultipleInputs,
  } = useFormHook<FormShape>(
    flattenData(
      paymentDataFields,
      creditCardData,
      achData,
      paymentIssuerSource,
    ),
    handleFormSubmit,
    makeValidationSchema(today, twoMonthsAhead),
    { inputDependencies, deferErrorCheck: true },
  );

  useEffect(() => {
    if (exclusionDate !== '') {
      getInputProps('policyStartDate').onChange(exclusionDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exclusionDate]);

  useEffect(() => {
    if (requestFractionationStatus === 'success') {
      const paymentQuantity = getInputProps('paymentQuantity').value as number;
      const paymentTypeForm = getInputProps('type').value as string;
      const fraction = findFractionByQuantity(
        fractionations,
        paymentQuantity,
        paymentTypeForm,
        profileType,
      );
      updatePaymentData(getInputProps, fraction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestFractionationStatus]);

  const collectionAuthorization = getInputProps('collectionAuthorization')
    .value as boolean;
  const paymentType = getInputProps('type').value as string;

  const paymentIssuerSourceForm = getInputProps('paymentIssuerSource')
    .value as string;

  /**
   * Whether the button should be disabled
   */
  const buttonIsDisabled = useMemo(
    () =>
      (paymentType === creditCard && !collectionAuthorization) ||
      (paymentType !== creditCard && additionalDocumentACHBase64 === '') ||
      requestFractionationStatus !== 'success' ||
      !providedAllRequired(),
    [
      collectionAuthorization,
      requestFractionationStatus,
      providedAllRequired,
      paymentType,
      additionalDocumentACHBase64,
    ],
  );

  // Copy link action logic
  const handleCopyPaymentLink = (): void => {
    const baseAppUrl = `${process.env.REACT_APP_INSPECTION_URL}`;
    const link = `${baseAppUrl.replace(
      '?id=',
      '',
    )}emission/continue?accepted=${accepted}&uniqueId=${uniqueId}`;
    const clipboardMessage = `Puedes ingresar al siguiente enlace para hacer el pago de tu póliza de auto con ASSA:\n${link}`;
    copy(clipboardMessage);
    setOpenCopySnack(true);
  };

  const hideOtherOptions = verifyBranchOffice(quotation);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid {...gridProps} className={wrapperClasses.pageContainer}>
          <Grid item xs={12}>
            <SignAlertMessage />
          </Grid>
          <Grid item xs={12}>
            <PaymentMessageAlert />
          </Grid>

          {/* Payment issuer */}
          <PaymentIssuerSelectionCards
            data-testid="issuer-selection-cards"
            getInputProps={getInputProps}
            personValidateData={personValidateData}
            validatePersonAction={validatePersonAction}
            currentIssuer={curPaymentIssuer}
            onPaymentIssuerChanged={(newIssuer): void => {
              updateCurPaymentIssuer(newIssuer);
            }}
            setNewPayerDataFieldsAction={setNewPayerDataFieldsAction}
          />

          {/* Payment type */}
          <Grid item xs={12}>
            {PaymentMethodForm({
              getInputProps,
              updateMultipleInputs,
              moneyType: quotation.quotationProfile.typeOfMoney,
              fractionations,
              hideOtherOptions,
              profileType,
            })}
          </Grid>

          {/* Payment quantity */}
          <Grid item xs={12}>
            <Typography variant="body1" style={{ textAlign: 'center' }}>
              ¿Cómo realizarás el pago?
            </Typography>
          </Grid>
          {makePaymentQuantityCard({
            getInputProps,
            quotation,
            fractionations,
            isLoading: requestFractionationStatus === 'requesting',
            setPaymentFraction,
          })}

          {profileType === profiles.RETENCION && (
            <Grid
              container
              xs={12}
              justifyContent="center"
              alignContent="center"
              alignItems="center"
              style={{ marginTop: 10, marginBottom: 10 }}
            >
              <img
                src="../svg/gift.svg"
                alt="gift"
                style={{ marginRight: 10 }}
              />
              <Typography className={classes.autoPxMessage}>¡con</Typography>
              <img
                src="../svg/autoPX.svg"
                alt="autoPX"
                style={{ marginLeft: 5, marginRight: 5 }}
              />
              <Typography className={classes.autoPxMessage}>
                el primer mes de tu póliza será GRATIS! (Solo el primer año)
              </Typography>
            </Grid>
          )}

          {/* Additional paymente methods input */}
          {paymentType !== creditCard && (
            <>
              <Grid item xs={12}>
                <PhotoCard
                  id="sales-web-inspection-button-payment-support"
                  data-testid="payment-support-card"
                  title="Añade aquí el comprobante de pago"
                  subTitle=""
                  fileName={additionalDocumentACHFileName}
                  base64String={additionalDocumentACHBase64}
                  onLocalImageCaptured={setAdditionalDocumentACHAction}
                  confirmationMessage="¿Estás seguro que deseas eliminar el documento?"
                  requestStatus={paymentFileStatus}
                />
              </Grid>

              <Grid item xs={12}>
                <MessageBox
                  data-testid="warning-payment-support"
                  variant="info"
                  message={
                    <span
                      className={classes.copyLinkMessage}
                      data-testid="pament-support-prompt"
                    >
                      El pago debe acreditarse en las próximas 24 horas. En caso
                      contrario, la póliza podría cancelarse.
                    </span>
                  }
                  hideCloseButton
                />
              </Grid>
            </>
          )}

          {/* Policy Start Date */}
          <Grid item xs={12}>
            {makePolicyStartDateField(
              getInputProps,
              today,
              twoMonthsAhead,
              quotation.productId,
              paymentFraction,
              paymentType,
            )}
          </Grid>

          {/* Buttons */}
          {paymentIssuerSourceForm === 'other' && (
            <Grid item xs={12}>
              <MessageBox
                data-testid="warning-copy-link"
                variant="info"
                message={
                  <span
                    className={classes.copyLinkMessage}
                    data-testid="customer-name-prompt"
                  >
                    {`Al hacer clic en "Copiar link de pago", puedes enviar este link a tu cliente
                por WhatsApp o por email, para que realice el pago`}
                  </span>
                }
                hideCloseButton
              />
            </Grid>
          )}

          {/* The submit button */}
          <Grid item container xs={12}>
            <Grid item xs={12} lg={6} container justifyContent="center">
              <div className={wrapperClasses.buttonContainer}>
                <DefaultButton
                  id="copy-payment-link-button"
                  data-testid="copy-payment-link-button"
                  fullWidth
                  onClick={handleCopyPaymentLink}
                  variant="outlined"
                  customColor={{
                    default: colors.bright_turquoise,
                    hover: colors.onahau,
                    active: colors.white,
                  }}
                >
                  <Copy width={24} height={24} /> &nbsp;&nbsp; copiar link de
                  pago
                </DefaultButton>
              </div>
            </Grid>

            <Grid item xs={12} lg={6} container justifyContent="center">
              <div className={wrapperClasses.buttonContainer}>
                {renderSubmitButton(handleSubmit, buttonIsDisabled)}
              </div>
            </Grid>
          </Grid>

          <SnackbarMessage
            data-testid="snackbar-copy-link"
            open={openCopySnack}
            message="Se ha copiado el link de pago"
            onClose={(): void => setOpenCopySnack(false)}
          />
        </Grid>
      </Grid>

      <ConsentModal
        data-testid="swi-ce-consent-modal"
        open={openModal}
        onClose={(): void => {
          setOpenModal(false);
        }}
        onAccept={handleEffectSubmit}
      />

      <UpsellingModal
        data-testid="swi-ce-upselling-modal"
        open={shouldOpenUpsellingModal(quotation.createdAt, openUpsellingModal)}
        onClose={(): void => {
          setOpenUpsellingModal(false);
        }}
        moneyType={quotation.quotationProfile.typeOfMoney}
        onUpselling={handleEffectUpselling}
      />
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  paymentDataFields: PaymentDataFields;
  quotation: Quotation;
  creditCardData: CreditCardData;
  achData: ACHData;
  paymentIssuerSource: string;
  paymentIssuer: PaymentIssuer;
  additionalDocumentACHFileName: string;
  additionalDocumentACHBase64: string;
  assaExecutive: string;
  exclusionDate: string;
  fractionations: Fraction[];
  personValidateData: PersonDataResponse;
} => ({
  additionalDocumentACHFileName:
    state.emissionPolicy.additionalDocumentACHFileName,
  additionalDocumentACHBase64: state.emissionPolicy.additionalDocumentACHBase64,
  paymentDataFields: state.emissionPolicy.paymentFields,
  quotation: state.quotation.quotation,
  creditCardData: state.creditCardData,
  achData: state.achData,
  paymentIssuerSource: state.emissionPolicy.paymentIssuerSource,
  paymentIssuer: state.emissionPolicy.paymentIssuer,
  assaExecutive: state.profile.profile.IdUser,
  exclusionDate: state.emissionPolicy.exclusionDate,
  fractionations: state.emissionPolicy.fractionations,
  personValidateData: state.personValidate.personValidateData,
});

const reduxActions = {
  updateAllValuesAction: updateAllValues,
  setPaymentDataAction: setPaymentData,
  setACHDataAction: setACHData,
  invalidateCreditCardAction: invalidateCreditCard,
  setPaymentIssuerAction: setPaymentIssuer,
  setAdditionalDocumentACHAction: setAdditionalDocumentACH,
  requestFractionationAction: requestFractionation,
  requestUpsellingAction: requestUpselling,
  validatePersonAction: requestSearchValidatePerson,
  setNewPayerDataFieldsAction: setNewPayerDataFields,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
  uniqueId: string;
  accepted: string;
}

export default connector(PaymentData);
